/**
 * 保険請求統計一覧 modal picker.
 *
 * Ports RA2 resources/views/rezepts/statistical_pdf_menu.blade.php:
 *   - Checkbox list of massagers (first row = "すべて")
 *   - Select-all header checkbox
 *   - output_option: include massager name/address/number in PDF
 *   - output_option2: include 生活保護
 *   - Submit → multi-massager PDF, each massager = 1 page, open in new tab
 *   - Validate: at least 1 checkbox must be ticked
 *
 * Reuses useStatisticalMassagersQuery from Slice 2 — no extra endpoint needed.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppButton, AppCheckbox, AppModal } from '@/components/atoms';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import useAppToast from '@/hooks/useAppToast';
import type { StatisticalMassagerOption } from '../types';

interface AllRow {
  id: 'all';
  serial_number: null;
  name: 'すべて';
}

type Row = AllRow | StatisticalMassagerOption;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  yearmonth: string;
  massagers: StatisticalMassagerOption[];
  serviceType: string;
  onPrint: (payload: {
    yearmonth: string;
    massagerIds: (number | 'all')[];
    outputOption: boolean;
    outputOption2: boolean;
  }) => Promise<void>;
  isPrinting: boolean;
}

const ALL_ID = 'all' as const;
const ROWS_PER_PAGE = 5;

const StatisticalReportPdfMenuModal: React.FC<Props> = ({
  isOpen,
  onClose,
  yearmonth,
  massagers,
  serviceType,
  onPrint,
  isPrinting,
}) => {
  const toast = useAppToast();
  const massagerLabel = serviceType === SERVICE_TYPE_A2 ? 'はり・きゅう師' : 'マッサージ師';

  const rows: Row[] = useMemo(
    () => [{ id: ALL_ID, serial_number: null, name: 'すべて' }, ...massagers],
    [massagers],
  );

  const [checked, setChecked] = useState<Set<number | 'all'>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [outputOption, setOutputOption]   = useState(true);
  const [outputOption2, setOutputOption2] = useState(true);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setChecked(new Set());
      setCurrentPage(1);
      setOutputOption(true);
      setOutputOption2(true);
    }
  }, [isOpen]);

  const isAllChecked = checked.has(ALL_ID);
  const checkedMassagerIds = useMemo(
    () => Array.from(checked).map(id => (id === ALL_ID ? 'all' : String(id))).join(','),
    [checked],
  );
  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const shouldShowPagination = rows.length >= ROWS_PER_PAGE;
  const displayStart = rows.length === 0 ? 0 : (currentPage - 1) * ROWS_PER_PAGE + 1;
  const displayEnd = Math.min(currentPage * ROWS_PER_PAGE, rows.length);
  const pageNumbers = useMemo(
    () => Array.from({ length: totalPages }, (_, index) => index + 1),
    [totalPages],
  );
  const pageRows = useMemo(
    () => rows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE),
    [currentPage, rows],
  );

  const toggleAll = useCallback((val: boolean) => {
    if (val) {
      setChecked(new Set(rows.map(r => r.id) as (number | 'all')[]));
    } else {
      setChecked(new Set());
    }
  }, [rows]);

  const toggleRow = useCallback((id: number | 'all', val: boolean) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (val) {
        next.add(id);
        // If all individual items are checked, also check the "all" row
        const individualIds = rows.filter(r => r.id !== ALL_ID).map(r => r.id);
        if (individualIds.every(id => next.has(id as number))) {
          next.add(ALL_ID);
        }
      } else {
        next.delete(id);
        next.delete(ALL_ID); // uncheck select-all if any individual unchecked
      }
      return next;
    });
  }, [rows]);

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const handlePrint = useCallback(async () => {
    if (checked.size === 0) {
      toast.error(`${massagerLabel}を選択してください。`);
      return;
    }
    const massagerIds = Array.from(checked) as (number | 'all')[];
    await onPrint({ yearmonth, massagerIds, outputOption, outputOption2 });
  }, [checked, yearmonth, outputOption, outputOption2, onPrint, toast, massagerLabel]);

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="保険請求統計一覧"
      maxWidth={500}
      footer={[
        <AppButton key="cancel" type="default" onClick={onClose}>
          閉じる
        </AppButton>,
        <AppButton
          key="print"
          type="primary"
          disabled={isPrinting}
          loading={isPrinting}
          onClick={handlePrint}
        >
          印刷
        </AppButton>,
      ]}
    >
      <input type="hidden" id="massager_id" name="massager_id" value={checkedMassagerIds} />
      {/* Massager checkbox list */}
      <div className="max-h-72 overflow-y-auto border border-gray-200 rounded">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-100">
            <tr>
              <th className="px-2 py-1 w-8 text-center">
                <AppCheckbox
                  checked={isAllChecked}
                  onChange={e => toggleAll(e.target.checked)}
                />
              </th>
              <th className="px-2 py-1 text-left">{massagerLabel}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(row => (
              <tr key={String(row.id)} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-2 py-1 text-center">
                  <AppCheckbox
                    checked={checked.has(row.id as number | 'all')}
                    onChange={e => toggleRow(row.id as number | 'all', e.target.checked)}
                  />
                </td>
                <td className="px-2 py-1">
                  {/* HIT-4358: show the per-clinic serial_number, not the global massager id
                      (selection below still uses row.id). */}
                  {row.id === ALL_ID
                    ? 'すべて'
                    : `${row.serial_number ?? ''}-${row.name}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-sm">
        <span>{`${rows.length} 件中 ${displayStart}件から${displayEnd}件 を表示しています`}</span>
        {shouldShowPagination && (
          <div className="flex items-center gap-2">
            <AppButton
              size="small"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
            >
              {'<'}
            </AppButton>
            {pageNumbers.map(page => (
              <AppButton
                key={page}
                size="small"
                disabled={currentPage === page}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </AppButton>
            ))}
            <AppButton
              size="small"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            >
              {'>'}
            </AppButton>
          </div>
        )}
      </div>

      {/* Options */}
      <div className="mt-3 flex flex-col gap-2">
        <AppCheckbox
          checked={outputOption}
          onChange={e => setOutputOption(e.target.checked)}
        >
          請求書一覧に・{massagerLabel}番号・{massagerLabel}名・住所(店舗名)を印字する
        </AppCheckbox>
        <AppCheckbox
          checked={outputOption2}
          onChange={e => setOutputOption2(e.target.checked)}
        >
          生活保護を含む
        </AppCheckbox>
      </div>
    </AppModal>
  );
};

export default StatisticalReportPdfMenuModal;
